import React, { useEffect, useState } from 'react';
import PageHeader from '../../PageHeader/PageHeader';
import SingleFileUpload from '../../components/SingleFileUpload/SingleFileUpload';
import { HitApi } from '../../Api/ApiHit';
import { useDispatch, useSelector } from 'react-redux';
import { setApiJson } from '../../redux/Actions/ApiAction';
import AppTable from '../../components/AppTable/AppTable';
import DeleteButton from '../../components/Delete/DeleteButton';
import { setPagination } from '../../redux/Actions/TableAction';
import { Eye, Download, FileText, Video, Music, Copy, Check } from 'lucide-react';
import { deleteFile, searchFiles } from '../../constant/Constant';
import toast from 'react-hot-toast';

function FileUpload() {
    const dispatch = useDispatch();
    const ApiReducer = useSelector((state) => state.ApiReducer);
    const TableDataReducer = useSelector((state) => state.TableReducer);
    const [previewImage, setPreviewImage] = useState(null);
    const [copiedFileId, setCopiedFileId] = useState(null);
    const [imageLoadErrors, setImageLoadErrors] = useState(new Set());

    useEffect(() => {
        loadData();
    }, [TableDataReducer.pagination.page, TableDataReducer.pagination.limit]);

    const loadData = () => {
        const json = {
            page: TableDataReducer.pagination.page || 1,
            limit: TableDataReducer.pagination.limit || 10,
            search: {}
        };

        HitApi(json, searchFiles).then((res) => {
            console.log("res", res);
            if (res?.success && res?.statusCode === 200) {
                const files = res?.data || [];
                dispatch(setApiJson(files));

                const paginationData = {
                    total: res?.data?.totalDocs || 0,
                    page: TableDataReducer.pagination.page || 1,
                    limit: TableDataReducer.pagination.limit || 10
                };

                dispatch(setPagination(paginationData));
            }
        }).catch(error => {
            console.error("Error fetching files:", error);
        });
    };

    const handleDelete = (row) => {
        console.log("Delete file:", row);

        HitApi({ _id: row._id }, deleteFile)
            .then((res) => {
                if (res.success) {
                    toast.success(res.message || "File deleted successfully");
                    loadData(); // refresh the list
                } else {
                    toast.error(res.message || "Failed to delete file");
                }
            })
            .catch(error => {
                console.error("Error deleting file:", error);
                toast.error("Something went wrong while deleting the file");
            });
    };

    // const handleDownload = (url, filename) => {
    //     const link = document.createElement('a');
    //     link.href = url;
    //     link.download = filename;
    //     document.body.appendChild(link);
    //     link.click();
    //     document.body.removeChild(link);
    // };


    const handleDownload = async (url, filename) => {
    try {
        const response = await fetch(url, { mode: 'cors' });
        if (!response.ok) throw new Error('Network response was not ok');

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename || 'downloaded-file';
        document.body.appendChild(link);
        link.click();
        link.remove();

        // Cleanup
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
        console.error('Download failed:', error);
        toast.error("Failed to download file");
    }
};

    const handlePreview = (file) => {
        if (file.fileType === 'image') {
            setPreviewImage(file);
        } else {
            // For non-image files, open in new tab
            window.open(file.url, '_blank');
        }
    };

    const handleCopyLink = async (url, fileId) => {
        try {
            await navigator.clipboard.writeText(url);
            setCopiedFileId(fileId);
            // Reset the copied state after 2 seconds
            setTimeout(() => setCopiedFileId(null), 2000);
        } catch (err) {
            console.error('Failed to copy: ', err);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = url;
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
                document.execCommand('copy');
                setCopiedFileId(fileId);
                setTimeout(() => setCopiedFileId(null), 2000);
            } catch (fallbackErr) {
                console.error('Fallback copy failed: ', fallbackErr);
            }
            document.body.removeChild(textArea);
        }
    };

    const handleImageError = (fileId) => {
        setImageLoadErrors(prev => new Set([...prev, fileId]));
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getFileIcon = (fileType) => {
        switch (fileType) {
            case 'image':
                return <div className="w-3 h-3 rounded-full bg-green-500"></div>;
            case 'video':
                return <Video className="w-4 h-4 text-blue-500" />;
            case 'audio':
                return <Music className="w-4 h-4 text-purple-500" />;
            case 'document':
                return <FileText className="w-4 h-4 text-red-500" />;
            default:
                return <div className="w-3 h-3 rounded-full bg-gray-500"></div>;
        }
    };

    // Define table headers
    const tableHeaders = [
        {
            title: "S.No",
            key: "serialNumber",
            width: "80px",
            align: "center"
        },
        {
            title: "Preview",
            key: "preview",
            width: "100px",
            align: "center",
            render: (value, row) => (
                <div className="flex justify-center">
                    {row.fileType === 'image' && !imageLoadErrors.has(row._id) ? (
                        <img
                            src={row.url}
                            alt={row.originalName}
                            className="w-12 h-12 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => handlePreview(row)}
                            onError={() => handleImageError(row._id)}
                        />
                    ) : (
                        <div
                            className="w-12 h-12 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors"
                            onClick={() => handlePreview(row)}
                        >
                            {getFileIcon(row.fileType)}
                        </div>
                    )}
                </div>
            )
        },

        {
            title: "Title",
            key: "title",
            render: (value) => (
                <span className="text-gray-800 truncate max-w-xs block" title={value}>
                    {value}
                </span>
            )
        },
        {
            title: "Folder",
            key: "folder",
            render: (value) => (
                <span className="text-gray-800 truncate max-w-xs block" title={value}>
                    {value}
                </span>
            )
        },
        {
            title: "Category",
            key: "category",
            render: (value) => (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${value === 'image' ? 'bg-green-100 text-green-800' :
                    value === 'video' ? 'bg-blue-100 text-blue-800' :
                        value === 'document' ? 'bg-red-100 text-red-800' :
                            value === 'audio' ? 'bg-purple-100 text-purple-800' :
                                'bg-gray-100 text-gray-800'
                    }`}>
                    {value?.toUpperCase()}
                </span>
            )
        },
        {
            title: "Size",
            key: "size",
            render: (value) => (
                <span className="text-gray-600 text-sm">
                    {formatFileSize(value)}
                </span>
            )
        },
        {
            title: "Upload Date",
            key: "createdAt",
            render: (value) => (
                <span className="text-gray-600 text-sm">
                    {formatDate(value)}
                </span>
            )
        },
        {
            title: "Actions",
            key: "actions",
            align: "center",
            render: (value, row) => (
                <div className="flex justify-center space-x-1">
                    <button
                        onClick={() => handlePreview(row)}
                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-colors"
                        title="Preview file"
                    >
                        <Eye className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => handleDownload(row.url, row.originalName)}
                        className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-full transition-colors"
                        title="Download file"
                    >
                        <Download className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => handleCopyLink(row.url, row._id)}
                        className={`p-2 rounded-full transition-colors ${copiedFileId === row._id
                            ? 'text-green-600 bg-green-50'
                            : 'text-purple-600 hover:text-purple-800 hover:bg-purple-50'
                            }`}
                        title={copiedFileId === row._id ? "Link copied!" : "Copy link"}
                    >
                        {copiedFileId === row._id ? (
                            <Check className="w-4 h-4" />
                        ) : (
                            <Copy className="w-4 h-4" />
                        )}
                    </button>
                    <DeleteButton onDelete={() => handleDelete(row)} />
                </div>
            )
        }
    ];


    return (
        <div className='p-5'>
            <PageHeader
                title={'File Upload'}
                description={'Upload anything like images, GIFs, or videos'}
            />

            {/* File Upload Component */}
            <div className="mb-8">
                <SingleFileUpload onUploadSuccess={loadData} />
            </div>

            {/* Files Table */}
            <div >

                <AppTable
                    TH={tableHeaders}
                    TD={ApiReducer.apiJson || []}
                    isLoading={!ApiReducer.apiJson?.length}
                    emptyMessage="No files found"
                    buttonText="Upload File"
                />


            </div>

            {/* Image Preview Modal */}
            {previewImage && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
                    onClick={() => setPreviewImage(null)}
                >
                    <div className="relative max-w-4xl max-h-full">
                        <button
                            onClick={() => setPreviewImage(null)}
                            className="absolute -top-10 right-0 text-white hover:text-gray-300 text-2xl font-bold z-10"
                        >
                            ×
                        </button>
                        <img
                            src={previewImage.url}
                            alt={previewImage.originalName}
                            className="max-w-full max-h-full object-contain rounded-lg"
                            onClick={(e) => e.stopPropagation()}
                        />
                        <div className="absolute bottom-4 left-4 bg-black bg-opacity-75 text-white p-2 rounded">
                            <p className="font-medium">{previewImage.originalName}</p>
                            <p className="text-sm text-gray-300">
                                {formatFileSize(previewImage.size)} • {formatDate(previewImage.createdAt)}
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default FileUpload;